import React, { useState, useEffect } from "react";
import { HexColorPicker } from "react-colorful";
import { UIComponents } from "@/components/ui";
import { ColorSwatchPickerProps, HslColor, RgbColor } from "./type";

const hexToRgb = (hex: string): RgbColor | null => {
  const clean = hex.replace(/^#/, "");
  if (![3, 6].includes(clean.length)) {
    return null;
  }
  const full =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean;
  if (full.length !== 6) {
    return null;
  }
  const num = parseInt(full, 16);
  if (isNaN(num)) {
    return null;
  }
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
};

const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (n: number): string => n.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
};

const rgbToHsl = (r: number, g: number, b: number): HslColor => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) {
    return { h: 0, s: 0, l: Math.round(l * 100) };
  }
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === rn) {
    h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  } else if (max === gn) {
    h = ((bn - rn) / d + 2) / 6;
  } else {
    h = ((rn - gn) / d + 4) / 6;
  }
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

const hslToRgb = (h: number, s: number, l: number): RgbColor => {
  const hn = h / 360;
  const sn = s / 100;
  const ln = l / 100;
  if (sn === 0) {
    const v = Math.round(ln * 255);
    return { r: v, g: v, b: v };
  }
  const hue2rgb = (p: number, q: number, t: number): number => {
    let tt = t;
    if (tt < 0) {
      tt += 1;
    }
    if (tt > 1) {
      tt -= 1;
    }
    if (tt < 1 / 6) {
      return p + (q - p) * 6 * tt;
    }
    if (tt < 1 / 2) {
      return q;
    }
    if (tt < 2 / 3) {
      return p + (q - p) * (2 / 3 - tt) * 6;
    }
    return p;
  };
  const q = ln < 0.5 ? ln * (1 + sn) : ln + sn - ln * sn;
  const p = 2 * ln - q;
  return {
    r: Math.round(hue2rgb(p, q, hn + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, hn) * 255),
    b: Math.round(hue2rgb(p, q, hn - 1 / 3) * 255),
  };
};

const initRgb = (hex: string): RgbColor => {
  return hexToRgb(hex) ?? { r: 0, g: 0, b: 0 };
};

const initHsl = (hex: string): HslColor => {
  const rgb = hexToRgb(hex) ?? { r: 0, g: 0, b: 0 };
  return rgbToHsl(rgb.r, rgb.g, rgb.b);
};

const clamp = (val: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, val));

type ColorMode = "RGB" | "HSL" | "HEX";

const ColorSwatchPicker = ({
  value,
  onChange,
  disabled,
  label,
}: ColorSwatchPickerProps): React.ReactElement => {
  const [hex, setHex] = useState<string>(value);
  const [rgb, setRgb] = useState<RgbColor>(() => initRgb(value));
  const [hsl, setHsl] = useState<HslColor>(() => initHsl(value));
  const [hexInput, setHexInput] = useState<string>(value);
  const [mode, setMode] = useState<ColorMode>("RGB");

  useEffect(() => {
    setHex(value);
    setHexInput(value);
    setRgb(initRgb(value));
    setHsl(initHsl(value));
  }, [value]);

  const handlePickerChange = (newHex: string): void => {
    const upper = newHex.toUpperCase();
    setHex(upper);
    setHexInput(upper);
    const newRgb = hexToRgb(upper) ?? { r: 0, g: 0, b: 0 };
    setRgb(newRgb);
    setHsl(rgbToHsl(newRgb.r, newRgb.g, newRgb.b));
    onChange(upper);
  };

  const handleHexInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setHexInput(e.target.value);
  };

  const handleHexInputBlur = (): void => {
    const raw = hexInput.trim();
    const withHash = raw.startsWith("#") ? raw : `#${raw}`;
    const newRgb = hexToRgb(withHash);
    if (!newRgb) {
      setHexInput(hex);
      return;
    }
    const newHex = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
    setHex(newHex);
    setHexInput(newHex);
    setRgb(newRgb);
    setHsl(rgbToHsl(newRgb.r, newRgb.g, newRgb.b));
    if (newHex !== hex) {
      onChange(newHex);
    }
  };

  const handleRgbChange = (
    channel: keyof RgbColor,
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const parsed = parseInt(e.target.value, 10);
    const val = isNaN(parsed) ? 0 : clamp(parsed, 0, 255);
    const newRgb = { ...rgb, [channel]: val };
    setRgb(newRgb);
    const newHex = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
    setHex(newHex);
    setHexInput(newHex);
    setHsl(rgbToHsl(newRgb.r, newRgb.g, newRgb.b));
    onChange(newHex);
  };

  const handleHslChange = (
    channel: keyof HslColor,
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const parsed = parseInt(e.target.value, 10);
    const max = channel === "h" ? 360 : 100;
    const val = isNaN(parsed) ? 0 : clamp(parsed, 0, max);
    const newHsl = { ...hsl, [channel]: val };
    setHsl(newHsl);
    const newRgb = hslToRgb(newHsl.h, newHsl.s, newHsl.l);
    setRgb(newRgb);
    const newHex = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
    setHex(newHex);
    setHexInput(newHex);
    onChange(newHex);
  };

  const handleModeChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setMode(e.target.value as ColorMode);
  };

  const modeSelect = (
    <select
      value={mode}
      onChange={handleModeChange}
      className="h-8 rounded-10 border border-border bg-background text-xs px-1 cursor-pointer focus:outline-none focus:border-primary transition-all duration-200"
    >
      <option value="RGB">RGB</option>
      <option value="HSL">HSL</option>
      <option value="HEX">HEX</option>
    </select>
  );

  return (
    <UIComponents.popover>
      <UIComponents.popoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="flex items-center gap-2 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
        >
          <div
            className="w-8 h-8 rounded-[6px] border border-border"
            style={{ backgroundColor: hex }}
          />
          {label && (
            <span className="text-sm font-medium text-foreground">{label}</span>
          )}
        </button>
      </UIComponents.popoverTrigger>
      <UIComponents.popoverContent
        className="w-auto p-3 space-y-2 rounded-20 overflow-visible z-[1000] bg-white border border-border shadow-lg"
        sideOffset={6}
        side="bottom"
        align="start"
        avoidCollisions={true}
        collisionPadding={16}
      >
        <div className="color-picker-wrapper [&_.react-colorful]:!h-[160px]">
          <HexColorPicker color={hex} onChange={handlePickerChange} />
        </div>

        {mode === "RGB" && (
          <div className="space-y-1">
            <div className="flex items-start justify-between gap-2">
              <div className="w-[46px]">
                <UIComponents.input
                  value={String(rgb.r)}
                  onChange={(e): void => handleRgbChange("r", e)}
                  className="text-center min-h-8 h-8 px-1.5 text-xs"
                />
                <span className="block text-center text-xs text-muted-foreground mt-0.5">
                  R
                </span>
              </div>
              <div className="w-[46px]">
                <UIComponents.input
                  value={String(rgb.g)}
                  onChange={(e): void => handleRgbChange("g", e)}
                  className="text-center min-h-8 h-8 px-1.5 text-xs"
                />
                <span className="block text-center text-xs text-muted-foreground mt-0.5">
                  G
                </span>
              </div>
              <div className="w-[46px]">
                <UIComponents.input
                  value={String(rgb.b)}
                  onChange={(e): void => handleRgbChange("b", e)}
                  className="text-center min-h-8 h-8 px-1.5 text-xs"
                />
                <span className="block text-center text-xs text-muted-foreground mt-0.5">
                  B
                </span>
              </div>
              {modeSelect}
            </div>
          </div>
        )}

        {mode === "HSL" && (
          <div className="space-y-1">
            <div className="flex items-start justify-between gap-2">
              <div className="w-[46px]">
                <UIComponents.input
                  value={String(hsl.h)}
                  onChange={(e): void => handleHslChange("h", e)}
                  className="text-center min-h-8 h-8 px-1.5 text-xs"
                />
                <span className="block text-center text-xs text-muted-foreground mt-0.5">
                  H
                </span>
              </div>
              <div className="w-[46px]">
                <UIComponents.input
                  value={String(hsl.s)}
                  onChange={(e): void => handleHslChange("s", e)}
                  className="text-center min-h-8 h-8 px-1.5 text-xs"
                />
                <span className="block text-center text-xs text-muted-foreground mt-0.5">
                  S
                </span>
              </div>
              <div className="w-[46px]">
                <UIComponents.input
                  value={String(hsl.l)}
                  onChange={(e): void => handleHslChange("l", e)}
                  className="text-center min-h-8 h-8 px-1.5 text-xs"
                />
                <span className="block text-center text-xs text-muted-foreground mt-0.5">
                  L
                </span>
              </div>
              {modeSelect}
            </div>
          </div>
        )}

        {mode === "HEX" && (
          <div className="space-y-1">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <UIComponents.input
                  value={hexInput}
                  onChange={handleHexInputChange}
                  onBlur={handleHexInputBlur}
                  placeholder="#000000"
                  maxLength={7}
                  className="text-center min-h-8 h-8 px-1.5 text-xs"
                />
                <span className="block text-center text-xs text-muted-foreground mt-0.5">
                  HEX
                </span>
              </div>
              {modeSelect}
            </div>
          </div>
        )}
      </UIComponents.popoverContent>
    </UIComponents.popover>
  );
};

export default ColorSwatchPicker;
